import { useState, useEffect, useMemo } from 'react';
import { Tabs, Tab, TextField, InputAdornment } from '@mui/material';

import useFetchByTab from '../hooks/useFetchByTab';
import GenericTable from './GenericTable';
import AccordionTableGroup from './AccordionTableGroup';
import { useErpPa } from '../contexts/ErpPaProvider';
import { postCancelERPOrders } from '../api';
import SkeletonTable from './SkeletonTable';
import ModalPurchaseOrder from '../../../components/ui/organisms/faculty/ModalPurchaseOrder';
import CancelDialog from './CancelDialog';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import styled from 'styled-components';
import CardsPay from '../../../components/ui/organisms/faculty/CardsPay';
import { SearchRounded } from '@mui/icons-material';

const TabManager = ({ tabConfigs, newOrderSucces }) => {
    const { erpPaId } = useErpPa();

    const [selectedTab, setSelectedTab] = useState('nuevas_ordenes');
    const [currentKey, setCurrentKey] = useState('nuevas_ordenes');
    const [config, setConfig] = useState(tabConfigs['nuevas_ordenes']);

    const [filtersByColumn, setFiltersByColumn] = useState({});
    const [filterText, setFilterText] = useState('');
    const [sortField, setSortField] = useState(null);
    const [sortAsc, setSortAsc] = useState(true);

    const { ready } = useErpPa();

    const { data, loading, refetch } = useFetchByTab({
        key: currentKey,
        enabled: ready && (!tabConfigs[currentKey].requiresParam || !!erpPaId),
        asyncFn: () => {
            if (tabConfigs[currentKey].requiresParam) {
                if (!erpPaId) return null;
                return tabConfigs[currentKey].fetchFunction(Number(erpPaId));
            }
            return tabConfigs[currentKey].fetchFunction();
        },
        adapter: tabConfigs[currentKey].adapter,
    });
    useEffect(() => {
        setConfig(tabConfigs[selectedTab]);
        setCurrentKey(selectedTab);
        setFiltersByColumn({});
        setFilterText('');
        setSortField(null);
        setSortAsc(true);
    }, [selectedTab]);

    useEffect(() => {
        if (newOrderSucces) {
            if (currentKey === 'nuevas_ordenes') {
                refetch();
            }
            setSelectedTab('nuevas_ordenes');
        }
    }, [newOrderSucces]);

    const [openModal, setOpenModal] = useState(false);
    const [currentPreviewOrder, setCurrentPreviewOrder] = useState(null);
    const [isCancelModalOpen, setCancelModalOpen] = useState(false);
    const [rowToCancel, setRowToCancel] = useState(null);

    const handleOpenModal = (po) => {
        setCurrentPreviewOrder(po);
        setOpenModal(true);
    };

    const handleClose = () => {
        setCurrentPreviewOrder(null);
        setOpenModal(false);
    };

    const handleOpenCancelModal = (row) => {
        setRowToCancel(row);
        setCancelModalOpen(true);
    };

    const handleCloseCancelModal = () => {
        setRowToCancel(null);
        setCancelModalOpen(false);
    };

    const handleConfirmCancel = async () => {
        if (!rowToCancel) return;
        try {
            await postCancelERPOrders(rowToCancel.id);
            refetch();
        } catch (error) {
            console.error('Error al cancelar:', error);
        } finally {
            handleCloseCancelModal();
        }
    };

    //Acordeón de Órdenes de Pago, imprime toda la orden y solicitudes inlcluidas en ella si se busca una solicitud en particular
    const filteredAndSortedGroupData = useMemo(() => {
        if (!Array.isArray(data))
            return { orderedResult: [], dollar: 0, localCurrency: 0 };
        let result = [...data];

        // Monto en PAB/dolar
        const dollar = result
            .filter(
                (item) => item.currency !== 'ARS' && item.state === 'confirmed'
            )
            .reduce((acc, current) => acc + current.amount, 0);
        // Monto en ARS
        const localCurrency = result
            .filter(
                (item) => item.currency === 'ARS' && item.state === 'confirmed'
            )
            .reduce((acc, current) => acc + current.amount, 0);

        if (filterText) {
            result = result.filter((group) => {
                const groupText = JSON.stringify(group || {})
                    .toLowerCase()
                    .replace(/\s+/g, ' ');
                return groupText.includes(filterText.toLowerCase());
            });
        }

        const orderedResult = result.sort((a, b) => b.id - a.id);

        return {
            orderedResult,
            dollar,
            localCurrency,
        };
    }, [data, filterText]);

    //Lista de solicitudes _ filtra las canceladas y ordena segun columna
    const filteredAndSortedData = useMemo(() => {
        const groupList = Array.isArray(data?.orders) ? data.orders : [];

        let result = [...groupList];
        // Pagos a autorizar
        const draftForPay = result.filter(
            (item) => !item.invoiceIds?.length
        ).length;
        // Pagos autorizados
        const draftPay = result.filter(
            (item) => item.invoiceIds?.length
        ).length;

        if (filterText) {
            result = result.filter((group) => {
                const text = JSON.stringify(group || {})
                    .toLowerCase()
                    .replace(/\s+/g, ' ');
                return text.includes(filterText.toLowerCase());
            });
        }

        if (sortField) {
            result.sort((a, b) => {
                const valA = a[sortField] ?? '';
                const valB = b[sortField] ?? '';
                return sortAsc
                    ? String(valA).localeCompare(String(valB))
                    : String(valB).localeCompare(String(valA));
            });
        }

        const noCanceledResult = result?.filter(
            (row) => row.state !== 'cancel'
        );

        return {
            noCanceledResult,
            draftForPay,
            draftPay,
        };
    }, [data, filterText, sortField, sortAsc]);

    const handleHeaderClick = (accessor) => {
        if (accessor === sortField) {
            setSortAsc(!sortAsc);
        } else {
            setSortField(accessor);
            setSortAsc(true);
        }
    };

    const renderTabContent = () => {
        if (!ready) return <SkeletonTable />;
        if (config.type === 'accordion') {
            return (
                <AccordionTableGroup
                    rows={filteredAndSortedGroupData.orderedResult}
                    columnsHeader={config.columnsHeader}
                    columnsBody={config.columnsBody}
                />
            );
        }

        return (
            <GenericTable
                rows={filteredAndSortedData.noCanceledResult}
                loading={loading}
                handleOpenModal={handleOpenModal}
                handleCancelRow={handleOpenCancelModal}
                filtersByColumn={filtersByColumn}
                setFiltersByColumn={setFiltersByColumn}
                columns={config?.columns.map((col) => ({
                    ...col,
                    label: (
                        <span
                            onClick={() => handleHeaderClick(col.accessor)}
                            style={{ cursor: 'pointer' }}
                        >
                            {col.label}
                            {sortField === col.accessor ? (
                                sortAsc ? (
                                    <ArrowUpwardRoundedIconFake />
                                ) : (
                                    <ArrowDownwardRoundedIconFake />
                                )
                            ) : (
                                ''
                            )}
                        </span>
                    ),
                }))}
            />
        );
    };

    return (
        <>
            <Tabs
                value={selectedTab}
                onChange={(_, newValue) => setSelectedTab(newValue)}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons="auto"
            >
                {Object.entries(tabConfigs).map(([key, def]) => (
                    <Tab key={key} label={def.label} value={key} />
                ))}
            </Tabs>
            <SearchRow>
                <TextFieldStyled
                    label="Buscar en toda la tabla"
                    variant="outlined"
                    size="small"
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchRounded />
                            </InputAdornment>
                        ),
                    }}
                />
                {data && (
                    <CardsPay
                        localCurrency={
                            filteredAndSortedGroupData?.localCurrency || 0
                        }
                        dollar={filteredAndSortedGroupData?.dollar || 0}
                        authorization={filteredAndSortedData?.draftPay || 0}
                        draftPay={filteredAndSortedData?.draftForPay || 0}
                        type={config.type}
                    />
                )}
            </SearchRow>

            <ScrollArea>
                <TablePad>{renderTabContent()}</TablePad>
            </ScrollArea>

            {isCancelModalOpen && (
                <CancelDialog
                    open={isCancelModalOpen}
                    onClose={handleCloseCancelModal}
                    onConfirm={handleConfirmCancel}
                />
            )}
            {openModal && (
                <ModalPurchaseOrder
                    open={openModal}
                    handleClose={handleClose}
                    purchaseOrder={currentPreviewOrder}
                />
            )}
        </>
    );
};

export default TabManager;

const ArrowDownwardRoundedIconFake = styled(KeyboardArrowDownIcon)`
    &:hover {
        cursor: pointer;
    }
`;

const ArrowUpwardRoundedIconFake = styled(KeyboardArrowUpIcon)`
    &:hover {
        cursor: pointer;
    }
`;

const SearchRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: calc(100% - 4rem);
    padding: 2rem;
    justify-content: space-between;

    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

const TextFieldStyled = styled(TextField)`
    width: calc(40% - 2rem);
    padding: 1rem;
    background-color: #ffffff;
    border-radius: 100px;

    .MuiInputBase-root {
        color: #a8a8a8;
        font-weight: 500;
        border-radius: 100px;
    }
    fieldset {
        border-radius: 100px;
    }
    svg {
        fill: #616161;
        font-size: 24px;
        font-weight: 400;
    }
    @media (max-width: 768px) {
        width: calc(100% - 2rem);
    }
`;

const ScrollArea = styled.div`
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
    overflow-x: auto;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
`;

const TablePad = styled.div`
    min-width: 960px;
    padding: 0 2rem;
    box-sizing: border-box;

    @media (max-width: 768px) {
        min-width: 100%;
    }
    @media (max-width: 425px) {
        min-width: 350px;
    }
`;
